import { FC, useState } from 'react';
import {
    Modal,
    Steps,
    Button,
    Space,
    notification,
} from 'antd';
import { IoIosCloseCircle } from 'react-icons/io';

import { FileUploadStep } from './FileUploadStep';
import { PreviewStep } from './PreviewStep';
import { ResultsStep } from './ResultsStep';
import { BulkImportProps, BulkResult, ValidatedRow } from './types';

const BulkImport: FC<BulkImportProps> = ({
    open, onCancel, columns, validate, onImport, templateFileName, t,
}) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [validatedData, setValidatedData] = useState<ValidatedRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState<BulkResult | null>(null);

    const handleFileProcessed = (rows: ValidatedRow[]) => {
        setValidatedData(rows);
        setCurrentStep(1);
    };

    const handleImport = async () => {
        const validRows = validatedData.filter((d) => d._isValid);

        if (validRows.length === 0) {
            notification.warning({ message: t('BULK.NO_VALID_RECORDS') });
            return;
        }

        setLoading(true);

        try {
            const importResult = await onImport(validRows);
            setResult(importResult);
            setCurrentStep(2);
            notification.success({ message: t('BULK.IMPORT_SUCCESS') });
        } catch (error: any) {
            notification.error({
                message: 'Error',
                description: error.response?.data?.message || t('BULK.IMPORT_ERROR'),
            });
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        setCurrentStep(0);
        setValidatedData([]);
        setResult(null);
    };

    const handleClose = () => {
        handleReset();
        onCancel();
    };

    const validCount = validatedData.filter((d) => d._isValid).length;

    const steps = [
        { title: t('BULK.STEP_UPLOAD') },
        { title: t('BULK.STEP_PREVIEW') },
        { title: t('BULK.STEP_RESULTS') },
    ];

    return (
        <Modal
            centered
            open={open}
            onCancel={handleClose}
            title={t('BULK.TITLE')}
            width={1100}
            closeIcon={<IoIosCloseCircle size={20} color="var(--color-dark-2)" style={{ verticalAlign: 'middle' }} />}
            footer={null}
            styles={{ body: { padding: '16px 24px' } }}
        >
            <Space direction="vertical" size={24} style={{ width: '100%' }}>
                <Steps current={currentStep} items={steps} size="small" />

                {currentStep === 0 && (
                    <FileUploadStep
                        columns={columns}
                        templateFileName={templateFileName}
                        validate={validate}
                        onFileProcessed={handleFileProcessed}
                        t={t}
                    />
                )}

                {currentStep === 1 && (
                    <>
                        <PreviewStep data={validatedData} columns={columns} t={t} />
                        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                            <Button onClick={handleReset}>
                                {t('BULK.CHANGE_FILE')}
                            </Button>
                            <Space>
                                <Button onClick={handleClose}>
                                    {t('BULK.CANCEL')}
                                </Button>
                                <Button
                                    type="primary"
                                    loading={loading}
                                    onClick={handleImport}
                                    disabled={validCount === 0}
                                >
                                    {t('BULK.IMPORT_ACTION', { count: validCount })}
                                </Button>
                            </Space>
                        </Space>
                    </>
                )}

                {currentStep === 2 && result && (
                    <>
                        <ResultsStep result={result} t={t} />
                        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                            <Button onClick={handleReset}>
                                {t('BULK.IMPORT_MORE')}
                            </Button>
                            <Button type="primary" onClick={handleClose}>
                                {t('BULK.DONE')}
                            </Button>
                        </Space>
                    </>
                )}
            </Space>
        </Modal>
    );
};

export { BulkImport };
export type {
    BulkImportProps,
    BulkResult,
    BulkColumnConfig,
    ValidatedRow,
    ValidationError,
    TranslateFn,
} from './types';
